import { AnimatePresence, m } from "framer-motion";
import { useState, type KeyboardEvent } from "react";
import { Cancel01Icon, CentralIcon } from "~/components/icons/central-icons";
import { StackedTokenIcons } from "~/components/stacked-token-icons";
import {
  ErrorStatusDot,
  SuccessStatusDot,
  WarningStatusDot,
} from "~/components/status-dot";
import { TokenIcon } from "~/components/token-icon";
import { fluidSprings } from "~/components/ui/fluid-motion";
import { ProgressRing } from "~/components/ui/progress-ring";
import { m as messages } from "~/lib/i18n";
import {
  TOAST_PILL_ENTER_FROM,
  TOAST_PILL_EXIT,
  TOAST_PILL_LAYOUT_TRANSITION,
  TOAST_PILL_PULSE_TRANSITION,
  TOAST_PILL_RISE,
} from "./pill-motion";
import type { ExecutionToastView } from "./toast-view-model";

/** Radial tint fading over the surface base — mint while working/succeeded,
 * amber for waits, destructive on failure (v1's glow, on theme tokens). */
const TONE_TINTS: Record<ExecutionToastView["tone"], string> = {
  progress:
    "color-mix(in oklab, var(--brand-green-readable) 24%, transparent)",
  success:
    "color-mix(in oklab, var(--brand-green-readable) 24%, transparent)",
  warning: "color-mix(in oklab, var(--glider-highlight) 22%, transparent)",
  error: "color-mix(in oklab, var(--destructive) 20%, transparent)",
};

function StatusIndicator({ view }: { view: ExecutionToastView }) {
  if (view.status === "loading") {
    return (
      <span className="block text-status-positive-foreground">
        <ProgressRing size={32} strokeWidth={2.5} />
      </span>
    );
  }
  if (view.status === "success") {
    return <SuccessStatusDot />;
  }
  if (view.status === "error" || view.status === "cancelled") {
    return <ErrorStatusDot />;
  }
  return <WarningStatusDot />;
}

interface ExecutionToastPillProps {
  isFrontmost: boolean;
  isStackHovered: boolean;
  onDismiss: () => void;
  onExpand: () => void;
  stackTotal: number;
  view: ExecutionToastView;
}

/**
 * The collapsed center-toast pill (v1's TransactionNotification toast mode):
 * token icon, tensed label, status indicator, "+N" stack badge on the
 * frontmost pill, hover dismiss badge. Click expands to the modal.
 */
export function ExecutionToastPill({
  isFrontmost,
  isStackHovered,
  onDismiss,
  onExpand,
  stackTotal,
  view,
}: ExecutionToastPillProps) {
  const tint = TONE_TINTS[view.tone];
  const [isPillHovered, setIsPillHovered] = useState(false);
  const [isDismissFocused, setIsDismissFocused] = useState(false);
  const revealDismiss = isPillHovered || isDismissFocused;

  const handleExpandKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onExpand();
    }
  };

  return (
    <m.div
      animate={{
        opacity: 1,
        y: 0,
        scale: view.status === "success" ? [1, 1.04, 1] : 1,
      }}
      className="relative"
      data-operation-id={view.operationId}
      data-status={view.status}
      data-testid="execution-toast-pill"
      exit={TOAST_PILL_EXIT}
      initial={TOAST_PILL_ENTER_FROM}
      layout
      onHoverEnd={() => setIsPillHovered(false)}
      onHoverStart={() => setIsPillHovered(true)}
      transition={{
        ...TOAST_PILL_RISE,
        layout: TOAST_PILL_LAYOUT_TRANSITION,
        scale: TOAST_PILL_PULSE_TRANSITION,
      }}
    >
      <m.div
        aria-label={view.label}
        className="relative cursor-pointer overflow-hidden rounded-full bg-surface-4 shadow-surface-4 glider-focus-ring"
        onClick={onExpand}
        onKeyDown={handleExpandKeyDown}
        role="button"
        tabIndex={0}
        transition={fluidSprings.fast}
        whileHover={{ y: -2, scale: 1.01 }}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-full"
          style={{
            background: `radial-gradient(135% 135% at 50% -102%, ${tint} 0%, transparent 100%)`,
          }}
        />

        <div className="relative flex items-center justify-center gap-3 px-4 py-2">
          {view.tokenIcons && view.tokenIcons.length > 1 ? (
            <StackedTokenIcons size={38} tokens={view.tokenIcons} />
          ) : (
            <TokenIcon
              alt={view.iconSymbol ?? messages.toast_execution_pill_asset_alt()}
              chainId={view.chainId ?? undefined}
              disableLink
              hideChainBadge={view.chainId === null}
              size={38}
              src={view.iconUrl ?? undefined}
            />
          )}

          <AnimatePresence mode="wait">
            <m.span
              animate={{ opacity: 1, y: 0 }}
              className="text-meta font-medium whitespace-nowrap text-foreground"
              exit={{ opacity: 0, y: -4, transition: fluidSprings.fast.exit }}
              initial={{ opacity: 0, y: 4 }}
              key={view.status}
              transition={fluidSprings.fast}
            >
              {view.label}
            </m.span>
          </AnimatePresence>

          <div className="shrink-0">
            <AnimatePresence mode="wait">
              <m.div
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: 0,
                  scale: 0.8,
                  transition: fluidSprings.fast.exit,
                }}
                initial={{ opacity: 0, scale: 0.8 }}
                key={view.status}
                transition={fluidSprings.fast}
              >
                <StatusIndicator view={view} />
              </m.div>
            </AnimatePresence>
          </div>
        </div>
      </m.div>

      {isFrontmost && stackTotal > 1 && !isStackHovered ? (
        <div className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-foreground shadow-lg">
          <span className="text-caption font-semibold text-background">
            +{stackTotal - 1}
          </span>
        </div>
      ) : null}

      <m.button
        animate={{
          opacity: revealDismiss ? 1 : 0,
          scale: revealDismiss ? 1 : 0.75,
        }}
        aria-label={messages.toast_execution_pill_dismiss_aria_label()}
        className="absolute -top-1 -right-1 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-foreground shadow-lg glider-focus-ring"
        data-testid="execution-toast-dismiss"
        initial={false}
        onBlur={() => setIsDismissFocused(false)}
        onClick={(event) => {
          event.stopPropagation();
          onDismiss();
        }}
        onFocus={() => setIsDismissFocused(true)}
        style={{ pointerEvents: revealDismiss ? "auto" : "none" }}
        transition={fluidSprings.fast}
        type="button"
      >
        <CentralIcon className="size-3 text-background" icon={Cancel01Icon} />
      </m.button>
    </m.div>
  );
}
